import { v4 as uuidV4 } from 'uuid';
import { com as progressMp } from '@rosetta/gaia-progress-mp';
import { getGaiaApolloClient, getConfig, getUserAgent } from '../init';
import { progressActions } from './redux/progress-reducer';
import { getReduxStore } from '../init';
import { progressStepTypes } from './progress-constants';

var domainModelFactory = progressMp.rosettastone.gaia.progress.module.ProgressModuleJs.ProgressModuleCompanion.getProgressDomainModelFactory();
var progressManager = void 0;
var progressNotifier = void 0;
var progressTracker = void 0;

var getProgressManager = function getProgressManager() {
  return progressManager;
};
var getProgressTracker = function getProgressTracker() {
  return progressTracker;
};
var getProgressNotifier = function getProgressNotifier() {
  return progressNotifier;
};
var getProgressDomainModelFactory = function getProgressDomainModelFactory() {
  return domainModelFactory;
};

// On errors, Progress-MP resolves a promise returning an exception that must be dispatched
// into the redux intrastructure.
var dispatchMpThrowableToRedux = function dispatchMpThrowableToRedux(context) {
  return function (throwable) {
    return getReduxStore().dispatch(progressActions.dispatchProgressMpError(context, throwable));
  };
};

/**
 * Invoked at login to bind the user+server to the shared MP library.
 */
var userIdentified = function userIdentified(userId, accessToken) {
  createProgressManager(userId, accessToken, getConfig().serverUrl + '/graphql', getUserAgent());
  sessionStarted(function () {
    loadProgressTracker();
    loadProgressNotifier();
    progressManager.syncRemoteProgress(function (rollupDeltaMapModel) {
      getReduxStore().dispatch(progressActions.setLoadingProgressMpLibrary(false));
    }, dispatchMpThrowableToRedux('syncRemoteProgress()'));
  });
};

/**
 * Invoked to flush data from the local IndexDB.
 */
var flushIndexedDb = function flushIndexedDb() {
  progressManager.clearProgress(true, function () {
    getGaiaApolloClient().clearStore();
  }, dispatchMpThrowableToRedux('flushIndexedDb()'));
};

var removeSequenceProgressFromDb = function removeSequenceProgressFromDb(courseId, sequenceId, sequenceVersion) {
  var sequenceIdentifierModel = domainModelFactory.toSequenceIdentifierModel(courseId, sequenceId, sequenceVersion);
  progressManager.deleteSequenceProgress(sequenceIdentifierModel, function () {
    /* non-error completion routine */
  }, dispatchMpThrowableToRedux('removeSequenceProgressFromDb()'));
};

var createProgressManager = function createProgressManager(userId, accessToken, progressServerUrl, userAgent) {
  var clearDbOnInit = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;

  var progressConfiguration = {
    userId: userId,
    accessToken: accessToken,
    progressServerUrl: progressServerUrl,
    userAgent: userAgent
  };
  // options are: requestConcurrencyLimit, courseDescriptionCacheSize, networkTimeoutMs,
  //               databaseTimeoutMs, nullRollupIfNoProgress, maxLogLevel
  var progressManagerOptions = domainModelFactory.toProgressManagerOptionsJs(null, null, null, null, true, 'error');

  // args are:  config, options, externalLogger, useDiskPersistence, uuidProvider
  progressManager = progressMp.rosettastone.gaia.progress.module.ProgressModuleJs.ProgressModuleCompanion.getProgressManagerInstance(progressConfiguration, progressManagerOptions, {
    logRemote: function logRemote(context, errorMsg) {
      getReduxStore().dispatch(progressActions.dispatchProgressMpError(context, new Error(errorMsg)));
    }
  }, true, {
    getUuid: function getUuid() {
      return uuidV4();
    }
  });
  return progressManager;
};

var sessionStarted = function sessionStarted(initializationSuccessPromise) {
  progressManager.sessionStarted(false, initializationSuccessPromise, dispatchMpThrowableToRedux('sessionStarted()'));
};

var logout = function logout() {
  progressManager.sessionEnded();
};

var loadProgressTracker = function loadProgressTracker() {
  progressManager.getProgressTracker(function (aProgressTracker) {
    progressTracker = aProgressTracker;
  }, dispatchMpThrowableToRedux('loadProgressTracker()'));
};

var fetchLastSequenceAttempted = function fetchLastSequenceAttempted(courseId) {
  progressManager.getLastSequenceAttempted(courseId, function (sequenceIdentifierModel) {
    if (sequenceIdentifierModel) {
      getReduxStore().dispatch(progressActions.setLastSequenceAttempted(sequenceIdentifierModel.courseId, sequenceIdentifierModel.sequenceId));
    }
  }, dispatchMpThrowableToRedux('fetchLastSequenceAttempted()'));
};

// this gets the notifier from progress-mp-lib which emits an event just before
//  the lib sends new progress to the server.  GW subscribes in order to
//  update its internal progress data.
var loadProgressNotifier = function loadProgressNotifier() {
  progressManager.getProgressNotifier(function (aProgressNotifier) {
    progressNotifier = aProgressNotifier;
    progressNotifier.subscribeToAddProgressEvents({
      onUpdate: function onUpdate(progressAdded) {
        getReduxStore().dispatch(progressActions.handleProgressAdded(progressAdded));
      }
    });
  }, dispatchMpThrowableToRedux('loadProgressNotifier()'));
};

var stepStatusContextToActivityStepIdentifierModel = function stepStatusContextToActivityStepIdentifierModel(stepStatusContext) {
  return !!stepStatusContext ? domainModelFactory.toActivityStepIdentifierModel(stepStatusContext.courseId, stepStatusContext.sequenceId, stepStatusContext.sequenceVersion, stepStatusContext.activityId, stepStatusContext.activityStepId, stepStatusContext.activitySubStepId ? stepStatusContext.activitySubStepId : null) : null;
};

var stepStatusContextToActivityIdentifierModel = function stepStatusContextToActivityIdentifierModel(stepStatusContext) {
  return !!stepStatusContext ? domainModelFactory.toActivityIdentifierModel(stepStatusContext.courseId, stepStatusContext.sequenceId, stepStatusContext.sequenceVersion, stepStatusContext.activityId) : null;
};

var mapToAnswersIdListFormat = function mapToAnswersIdListFormat(answers) {
  var answerArray = answers.reduce(function (accum, answer) {
    accum.push(domainModelFactory.toBooleanStringPair(answer.correct, answer.answer));
    return accum;
  }, []);
  var result = domainModelFactory.toAnswerIdListFormat(domainModelFactory.toBooleanStringPairList(answerArray));
  return result;
};

var buildActivitySpecificAnswer = function buildActivitySpecificAnswer(activityPayload) {
  var progressStepType = activityPayload.progressStepType;

  /* Expectations from the Gaia-Progress-MP code:
      is SingleAnswerIdFormat ->
        when (activityStepTypeId) {
          ACTIVITY_TYPE_MULTIPLE_CHOICE,
          ACTIVITY_TYPE_DIALOGUE -> true
          else -> false
        }
      is FreeFormTextAnswerFormat ->
        when (activityStepTypeId) {
          ACTIVITY_TYPE_WRITING -> true
          else -> false
      }
      is AnswerIdListFormat ->
         when (activityStepTypeId) {
            ACTIVITY_TYPE_FILL_IN_THE_BLANK,
            ACTIVITY_TYPE_SEQUENCING -> true
            else -> false
       }
       is AnswerIdMapFormat ->
          when (activityStepTypeId) {
            ACTIVITY_TYPE_MATCHING -> true
            else -> false
       }
       is SubstepFormat ->
          when (activityStepTypeId) {
            ACTIVITY_TYPE_PRONUNCIATION,
            ACTIVITY_TYPE_VOCABULARY,
            ACTIVITY_TYPE_EXPLANATION -> true
            else -> false
        }
        is NoAnswerFormat -> {
          when (activityStepTypeId) {
            ACTIVITY_TYPE_EXTENDED_WRITING,
            ACTIVITY_TYPE_EXTENDED_SPEAKING -> true
  */

  switch (progressStepType) {
    case progressStepTypes.VOCABULARY_CARD:
    case progressStepTypes.REVIEW_ONLY_STEP_TYPE:
    case progressStepTypes.DEMONSTRATION:
    case progressStepTypes.EXPLANATION:
    case progressStepTypes.EXTENDED_WRITING:
    case progressStepTypes.EXTENDED_SPEAKING_RECORDING_DONE:
      return domainModelFactory.toNoAnswerFormat();
    case progressStepTypes.PRONUNCIATION_PHONEME:
      return domainModelFactory.toSreScoredAnswerFormat(activityPayload.correct ? 3 : 0);
    case progressStepTypes.PRONUNCIATION_WORD_SENTENCE:
    case progressStepTypes.MATCHING_BY_TEXT:
    case progressStepTypes.MATCHING_BY_MENU:
    case progressStepTypes.IMAGE_MATCHING:
    case progressStepTypes.SEQUENCING_VERTICAL:
    case progressStepTypes.TRANSFORMATION:
    case progressStepTypes.SEQUENCING_AUDIO:
    case progressStepTypes.SENTENCE_BUILDER_SEQUENCING:
    case progressStepTypes.CLOZE_SELECT:
    case progressStepTypes.CLOZE_DRAG_AND_DROP:
      return mapToAnswersIdListFormat(activityPayload.answers);
    case progressStepTypes.READ_ALOUD:
    case progressStepTypes.MULTIPLE_CHOICE_ASSESSMENT:
    case progressStepTypes.TYPE_THE_ANSWER:
    case progressStepTypes.CLOZE_TYPING:
    case progressStepTypes.CLOZE_SPEECH:
      return domainModelFactory.toSingleAnswerIdFormat(activityPayload.correct, activityPayload.answers && activityPayload.answers.length > 0 ? activityPayload.answers[0].answer : '');
    default:
      return null;
  }
};

export default {
  dispatchMpThrowableToRedux: dispatchMpThrowableToRedux,
  userIdentified: userIdentified,
  flushIndexedDb: flushIndexedDb,
  logout: logout,
  mapToAnswersIdListFormat: mapToAnswersIdListFormat,
  stepStatusContextToActivityIdentifierModel: stepStatusContextToActivityIdentifierModel,
  stepStatusContextToActivityStepIdentifierModel: stepStatusContextToActivityStepIdentifierModel,
  buildActivitySpecificAnswer: buildActivitySpecificAnswer,
  fetchLastSequenceAttempted: fetchLastSequenceAttempted,
  getProgressDomainModelFactory: getProgressDomainModelFactory,
  getProgressManager: getProgressManager,
  getProgressTracker: getProgressTracker,
  getProgressNotifier: getProgressNotifier,
  removeSequenceProgressFromDb: removeSequenceProgressFromDb
};